import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, ExternalLink, Flame, Leaf } from 'lucide-react'
import { Skeleton, ThreatCardSkeleton } from './Skeleton'
import { getSeverityClasses } from './severityStyles'
import { EmptyState } from '../ui/EmptyState'
import {
  useAddCompetitor,
  useEmergingBrands,
  useUpdateEmergingBrandDarkSkinRelevant,
  useUpdateEmergingBrandStatus,
} from '../../hooks/useCompetitiveData'
import type { EmergingBrandRow } from '../../types/competitive'

const STATUS_TABS = ['all', 'new', 'watching', 'dismissed'] as const

type StatusTab = (typeof STATUS_TABS)[number]

function sourceLabel(source: string): string {
  if (source === 'amazon_keywords') return 'Amazon Keywords'
  if (source === 'google_serp') return 'Google SERP'
  if (source === 'tiktok') return 'TikTok'
  return source.replace(/_/g, ' ')
}

function emergingStatusClasses(status: string): string {
  const normalized = status.toLowerCase()
  const severity = normalized === 'new' ? 'medium' : normalized === 'watching' ? 'watch' : 'neutral'
  const styles = getSeverityClasses(severity)
  return `border ${styles.border} ${styles.badge}`
}

function emergingScoreBarClasses(score: number): string {
  if (score > 80) {
    return 'bg-red-500'
  }
  if (score >= 60) {
    return 'bg-amber-500'
  }
  if (score >= 40) {
    return 'bg-blue-500'
  }
  return 'bg-emerald-500'
}

function normalizeEmergingScore(value: number | string | null | undefined): number {
  const parsed = Number(value ?? 0)
  if (!Number.isFinite(parsed)) {
    return 0
  }
  return Math.max(0, parsed)
}

function vitalityScore(brand: EmergingBrandRow): number {
  return Math.max(
    normalizeEmergingScore(brand.vitality_score),
    normalizeEmergingScore(brand.virality_score)
  )
}

function vitalityLabel(score: number): string {
  if (score >= 80) return 'Very active'
  if (score >= 55) return 'Moderate'
  if (score >= 30) return 'Low'
  return 'Minimal'
}

function isDarkSkinRelevant(brand: EmergingBrandRow, overrides?: Record<number, boolean>): boolean {
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, brand.id)) {
    return Boolean(overrides[brand.id])
  }
  return Boolean(brand.is_dark_skin_relevant ?? brand.dark_skin_relevant)
}

function detectedAt(brand: EmergingBrandRow): string | null {
  return brand.first_detected_at ?? brand.created_at ?? null
}

function filterByStatusTab(brand: EmergingBrandRow, statusTab: StatusTab): boolean {
  const status = String(brand.status ?? '').toLowerCase()

  if (statusTab === 'all') {
    return status === 'new' || status === 'watching'
  }

  return status === statusTab
}

function competitorPayload(brand: EmergingBrandRow) {
  const score = normalizeEmergingScore(brand.virality_score)
  return {
    brand_name: brand.brand_name,
    amazon_brand_name: brand.brand_name,
    tiktok_handle: brand.tiktok_handle ?? null,
    threat_tier: score > 70 ? 'active' : 'watch',
    notes: `Added from Emerging Brands radar (score ${score.toFixed(1)})`,
  }
}

export default function EmergingBrandsTab() {
  const queryClient = useQueryClient()
  const [statusTab, setStatusTab] = useState<StatusTab>('all')
  const [showEstablishedBrands, setShowEstablishedBrands] = useState(false)
  const [backgroundExpanded, setBackgroundExpanded] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [localError, setLocalError] = useState<string | null>(null)
  const [darkSkinOverrides, setDarkSkinOverrides] = useState<Record<number, boolean>>({})
  const [darkSkinSavingIds, setDarkSkinSavingIds] = useState<Record<number, boolean>>({})
  const [busyState, setBusyState] = useState<{ mode: 'single' | 'bulk'; action: 'dismiss' | 'add'; id?: number } | null>(null)

  const { data, isLoading, error } = useEmergingBrands()
  const updateStatusMutation = useUpdateEmergingBrandStatus()
  const updateDarkSkinRelevantMutation = useUpdateEmergingBrandDarkSkinRelevant()
  const addCompetitorMutation = useAddCompetitor()

  const allBrands = useMemo(() => {
    if (!data || !Array.isArray(data.brands)) {
      return []
    }
    return data.brands
  }, [data])

  const visibleBrands = useMemo(() => {
    const filtered = allBrands
      .filter((brand) => filterByStatusTab(brand, statusTab))
      .filter((brand) => showEstablishedBrands || !Boolean(brand.is_established))

    filtered.sort((left, right) => {
      const scoreDiff = vitalityScore(right) - vitalityScore(left)
      if (scoreDiff !== 0) {
        return scoreDiff
      }
      return Number(right.keyword_overlap_count ?? 0) - Number(left.keyword_overlap_count ?? 0)
    })

    return filtered
  }, [allBrands, showEstablishedBrands, statusTab])

  useEffect(() => {
    setBackgroundExpanded(false)
  }, [showEstablishedBrands, statusTab])

  const { watchList, recentlyDetected, background } = useMemo(() => {
    const watch: EmergingBrandRow[] = []
    const watchIdSet = new Set<number>()
    const recent: EmergingBrandRow[] = []
    const rest: EmergingBrandRow[] = []
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000

    for (const brand of visibleBrands) {
      const score = vitalityScore(brand)
      if (isDarkSkinRelevant(brand, darkSkinOverrides) && score > 80) {
        watch.push(brand)
      }
    }

    watch
      .sort((left, right) => vitalityScore(right) - vitalityScore(left))
      .slice(0, 10)
      .forEach((brand) => watchIdSet.add(brand.id))

    for (const brand of visibleBrands) {
      if (watchIdSet.has(brand.id)) {
        continue
      }
      const detected = detectedAt(brand)
      const detectedMs = detected ? new Date(detected).getTime() : Number.NaN
      if (Number.isFinite(detectedMs) && detectedMs >= sevenDaysAgo) {
        recent.push(brand)
      } else {
        rest.push(brand)
      }
    }

    return {
      watchList: watch.filter((brand) => watchIdSet.has(brand.id)),
      recentlyDetected: recent,
      background: rest,
    }
  }, [darkSkinOverrides, visibleBrands])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedBrands = useMemo(
    () => allBrands.filter((brand) => selectedSet.has(brand.id)),
    [allBrands, selectedSet]
  )

  const hasSelection = selectedIds.length > 0
  const queryError = error instanceof Error ? error.message : null
  const mergedError = localError || queryError
  const visibleTotalLabel = `${visibleBrands.length.toLocaleString('en-US')} shown`

  const updateSelection = (brandId: number, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        if (current.includes(brandId)) {
          return current
        }
        return [...current, brandId]
      }

      return current.filter((id) => id !== brandId)
    })
  }

  const clearSelection = () => {
    setSelectedIds([])
  }

  const toggleDarkSkinRelevant = async (brand: EmergingBrandRow, nextValue: boolean) => {
    const hadPreviousOverride = Object.prototype.hasOwnProperty.call(darkSkinOverrides, brand.id)
    const previousOverride = darkSkinOverrides[brand.id]

    setLocalError(null)
    setDarkSkinOverrides((current) => ({
      ...current,
      [brand.id]: nextValue,
    }))
    setDarkSkinSavingIds((current) => ({
      ...current,
      [brand.id]: true,
    }))

    try {
      const response = await updateDarkSkinRelevantMutation.mutateAsync({
        id: brand.id,
        is_dark_skin_relevant: nextValue,
      })

      setDarkSkinOverrides((current) => ({
        ...current,
        [brand.id]: Boolean(response.brand?.is_dark_skin_relevant),
      }))
    } catch (mutationError) {
      setDarkSkinOverrides((current) => {
        const next = { ...current }
        if (hadPreviousOverride) {
          next[brand.id] = previousOverride
        } else {
          delete next[brand.id]
        }
        return next
      })
      setLocalError(mutationError instanceof Error ? mutationError.message : 'Failed to update dark skin relevance')
    } finally {
      setDarkSkinSavingIds((current) => {
        const next = { ...current }
        delete next[brand.id]
        return next
      })
    }
  }

  const dismissBrand = async (brand: EmergingBrandRow) => {
    setBusyState({ mode: 'single', action: 'dismiss', id: brand.id })
    setLocalError(null)

    try {
      await updateStatusMutation.mutateAsync({ id: brand.id, status: 'dismissed' })
      setSelectedIds((current) => current.filter((id) => id !== brand.id))
    } catch (mutationError) {
      setLocalError(mutationError instanceof Error ? mutationError.message : 'Failed to dismiss brand')
    } finally {
      setBusyState(null)
    }
  }

  const addBrandAsCompetitor = async (brand: EmergingBrandRow) => {
    setBusyState({ mode: 'single', action: 'add', id: brand.id })
    setLocalError(null)

    try {
      await addCompetitorMutation.mutateAsync(competitorPayload(brand))
      await updateStatusMutation.mutateAsync({ id: brand.id, status: 'watching' })
      setSelectedIds((current) => current.filter((id) => id !== brand.id))
    } catch (mutationError) {
      setLocalError(mutationError instanceof Error ? mutationError.message : 'Failed to add competitor')
    } finally {
      setBusyState(null)
    }
  }

  const runBulkDismiss = async () => {
    if (selectedBrands.length === 0) {
      return
    }

    setBusyState({ mode: 'bulk', action: 'dismiss' })
    setLocalError(null)

    const failures: string[] = []

    for (const brand of selectedBrands) {
      try {
        await updateStatusMutation.mutateAsync({ id: brand.id, status: 'dismissed' })
      } catch (mutationError) {
        const message = mutationError instanceof Error ? mutationError.message : 'Dismiss failed'
        failures.push(`${brand.brand_name}: ${message}`)
      }
    }

    await queryClient.invalidateQueries({ queryKey: ['emerging-brands'] })
    clearSelection()

    if (failures.length > 0) {
      setLocalError(failures.slice(0, 2).join(' | '))
    }

    setBusyState(null)
  }

  const runBulkAddAsCompetitors = async () => {
    if (selectedBrands.length === 0) {
      return
    }

    setBusyState({ mode: 'bulk', action: 'add' })
    setLocalError(null)

    const failures: string[] = []

    for (const brand of selectedBrands) {
      try {
        await addCompetitorMutation.mutateAsync(competitorPayload(brand))
        await updateStatusMutation.mutateAsync({ id: brand.id, status: 'watching' })
      } catch (mutationError) {
        const message = mutationError instanceof Error ? mutationError.message : 'Add failed'
        failures.push(`${brand.brand_name}: ${message}`)
      }
    }

    await queryClient.invalidateQueries({ queryKey: ['emerging-brands'] })
    clearSelection()

    if (failures.length > 0) {
      setLocalError(failures.slice(0, 2).join(' | '))
    }

    setBusyState(null)
  }

  return (
    <section className="space-y-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Emerging Brands</h2>
          <p className="text-sm text-slate-500">Amazon-first emerging brand detection with melanin relevance scoring</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setStatusTab(tab)}
                className={`px-3 py-1.5 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                  statusTab === tab
                    ? 'bg-[var(--patterniq-navy-900)] text-white'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <label className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-blue-600"
              checked={showEstablishedBrands}
              onChange={(event) => setShowEstablishedBrands(event.target.checked)}
            />
            Show established brands
          </label>

          <span className="text-xs text-slate-500">{visibleTotalLabel}</span>
        </div>
      </div>

      {mergedError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{mergedError}</div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <ThreatCardSkeleton />
          <ThreatCardSkeleton />
          <ThreatCardSkeleton />
        </div>
      ) : visibleBrands.length === 0 ? (
        <EmptyState
          icon={<Flame className="h-6 w-6" />}
          title="No emerging brands yet"
          description="The keyword monitor runs daily and will surface high-momentum brands as data arrives."
        />
      ) : (
        <>
          <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Tier 1 — Watch List</h3>
              <span className="text-xs text-slate-500">{watchList.length} brands</span>
            </div>

            {watchList.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No brands currently meet watch-list criteria.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {watchList.map((brand) => {
                  const isChecked = selectedSet.has(brand.id)
                  const score = vitalityScore(brand)
                  const scoreColor = emergingScoreBarClasses(score)
                  const severity = getSeverityClasses(score > 80 ? 'critical' : score >= 60 ? 'high' : score >= 40 ? 'medium' : 'low')
                  const topTerms = (brand.keyword_overlap_terms ?? []).slice(0, 3)
                  const darkSkinRelevant = isDarkSkinRelevant(brand, darkSkinOverrides)
                  const darkSkinSaving = Boolean(darkSkinSavingIds[brand.id])
                  const singleActionBusy = busyState?.mode === 'single' && busyState.id === brand.id

                  return (
                    <article key={brand.id} className={`relative rounded-lg border p-4 shadow-sm ${severity.bg} ${severity.border}`}>
                      <label className="absolute right-3 top-3 inline-flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-blue-600"
                          checked={isChecked}
                          onChange={(event) => updateSelection(brand.id, event.target.checked)}
                          aria-label={`Select ${brand.brand_name}`}
                        />
                        Select
                      </label>

                      <h4 className="pr-16 text-base font-semibold text-slate-900">{brand.brand_name}</h4>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">
                          {sourceLabel(brand.detection_source)}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${emergingStatusClasses(brand.status)}`}>
                          {brand.status}
                        </span>
                        {darkSkinRelevant ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            <Leaf className="h-3 w-3" />
                            Dark Skin Relevant
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-3">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Vitality</p>
                        <p className="text-lg font-semibold text-slate-900">{score.toFixed(1)}</p>
                        <span className="text-xs text-slate-500">{vitalityLabel(score)}</span>
                        <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200">
                          <div className={`h-full rounded-full ${scoreColor}`} style={{ width: `${Math.min(score, 100)}%` }} />
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {topTerms.length === 0 ? (
                          <span className="text-xs text-slate-500">No keyword terms captured</span>
                        ) : (
                          topTerms.map((term) => (
                            <span key={term} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">
                              {term}
                            </span>
                          ))
                        )}
                      </div>

                      <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-blue-600"
                          checked={darkSkinRelevant}
                          disabled={darkSkinSaving}
                          onChange={(event) => void toggleDarkSkinRelevant(brand, event.target.checked)}
                        />
                        {darkSkinSaving ? 'Saving...' : 'Dark skin relevant'}
                      </label>

                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void dismissBrand(brand)}
                          disabled={singleActionBusy}
                          className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                        >
                          {singleActionBusy && busyState?.action === 'dismiss' ? 'Dismissing...' : 'Dismiss'}
                        </button>
                        <button
                          type="button"
                          onClick={() => void addBrandAsCompetitor(brand)}
                          disabled={singleActionBusy}
                          className="rounded-md border border-[var(--patterniq-navy-900)] bg-[var(--patterniq-navy-900)] px-2.5 py-1 text-xs font-medium text-white hover:bg-[var(--patterniq-navy-800)] disabled:opacity-60"
                        >
                          {singleActionBusy && busyState?.action === 'add' ? 'Adding...' : 'Add as Competitor'}
                        </button>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Tier 2 — Recently Detected</h3>
              <span className="text-xs text-slate-500">{recentlyDetected.length} brands</span>
            </div>

            {recentlyDetected.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No additional brands detected in the last 7 days.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {recentlyDetected.map((brand) => {
                  const isChecked = selectedSet.has(brand.id)
                  const score = vitalityScore(brand)
                  const scoreColor = emergingScoreBarClasses(score)
                  const darkSkinRelevant = isDarkSkinRelevant(brand, darkSkinOverrides)
                  const darkSkinSaving = Boolean(darkSkinSavingIds[brand.id])
                  const singleActionBusy = busyState?.mode === 'single' && busyState.id === brand.id
                  return (
                    <article key={brand.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{brand.brand_name}</h4>
                          <p className="text-xs text-slate-500">{sourceLabel(brand.detection_source)}</p>
                          {darkSkinRelevant ? (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              <Leaf className="h-3 w-3" />
                              Dark Skin Relevant
                            </span>
                          ) : null}
                        </div>
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-blue-600"
                          checked={isChecked}
                          onChange={(event) => updateSelection(brand.id, event.target.checked)}
                          aria-label={`Select ${brand.brand_name}`}
                        />
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                        <div className={`h-full rounded-full ${scoreColor}`} style={{ width: `${Math.min(score, 100)}%` }} />
                      </div>
                      <p className="mt-1 text-xs text-slate-600">Vitality {score.toFixed(1)} · {vitalityLabel(score)}</p>

                      <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-blue-600"
                          checked={darkSkinRelevant}
                          disabled={darkSkinSaving}
                          onChange={(event) => void toggleDarkSkinRelevant(brand, event.target.checked)}
                        />
                        {darkSkinSaving ? 'Saving...' : 'Dark skin relevant'}
                      </label>

                      <div className="mt-2 flex items-center gap-2">
                        {brand.amazon_url ? (
                          <a
                            href={brand.amazon_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-500 hover:underline"
                          >
                            Amazon
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => void addBrandAsCompetitor(brand)}
                          disabled={singleActionBusy}
                          className="rounded-md border border-[var(--patterniq-navy-900)] bg-[var(--patterniq-navy-900)] px-2 py-0.5 text-xs font-medium text-white hover:bg-[var(--patterniq-navy-800)] disabled:opacity-60"
                        >
                          {singleActionBusy && busyState?.action === 'add' ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <button
              type="button"
              onClick={() => setBackgroundExpanded((current) => !current)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Tier 3 — Background</h3>
                <p className="text-xs text-slate-500">{background.length} brands</p>
              </div>
              <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                {backgroundExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {backgroundExpanded ? 'Collapse' : 'Click to expand'}
              </span>
            </button>

            {backgroundExpanded ? (
              background.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  No background brands in this filter.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {background.map((brand) => {
                    const darkSkinRelevant = isDarkSkinRelevant(brand, darkSkinOverrides)
                    const darkSkinSaving = Boolean(darkSkinSavingIds[brand.id])

                    return (
                      <div key={brand.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        <p className="font-medium text-slate-900">{brand.brand_name}</p>
                        <p className="text-xs text-slate-500">
                          Vitality {vitalityScore(brand).toFixed(1)} · {vitalityLabel(vitalityScore(brand))} · {sourceLabel(brand.detection_source)}
                        </p>
                        {darkSkinRelevant ? (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                            <Leaf className="h-3 w-3" />
                            Dark Skin Relevant
                          </span>
                        ) : null}
                        <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-blue-600"
                            checked={darkSkinRelevant}
                            disabled={darkSkinSaving}
                            onChange={(event) => void toggleDarkSkinRelevant(brand, event.target.checked)}
                          />
                          {darkSkinSaving ? 'Saving...' : 'Dark skin relevant'}
                        </label>
                      </div>
                    )
                  })}
                </div>
              )
            ) : (
              <Skeleton height="h-3" className="w-56" />
            )}
          </section>
        </>
      )}

      {hasSelection ? (
        <div className="fixed bottom-4 left-1/2 z-40 w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-medium text-slate-800">{selectedIds.length} selected —</div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void runBulkDismiss()}
                disabled={busyState?.mode === 'bulk'}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
              >
                {busyState?.mode === 'bulk' && busyState.action === 'dismiss' ? 'Dismissing...' : 'Dismiss Selected'}
              </button>

              <button
                type="button"
                onClick={() => void runBulkAddAsCompetitors()}
                disabled={busyState?.mode === 'bulk'}
                className="rounded-md border border-[var(--patterniq-navy-900)] bg-[var(--patterniq-navy-900)] px-3 py-1.5 text-sm text-white hover:bg-[var(--patterniq-navy-800)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
              >
                {busyState?.mode === 'bulk' && busyState.action === 'add' ? 'Adding...' : 'Add as Competitors'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
